import { Box, Breakpoint, colors, Fade, Paper, styled } from "@mui/material";
import {
  CellFocusedEvent,
  CellPosition,
  ColDef,
  Column,
  ColumnResizedEvent,
  FilterChangedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  ICellRendererParams,
  IRowNode,
  PaginationChangedEvent,
  RowClassParams,
  RowNode,
  SelectionChangedEvent,
  SuppressHeaderKeyboardEventParams,
  SuppressKeyboardEventParams,
} from "ag-grid-community";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { autorun, IReactionDisposer, toJS } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import * as ReactDOM from "react-dom";
import "../agGridTheme/agGridTheme.scss";
import { State as AppServerState } from "../core/AppServer";
import FocusManager from "../core/FocusManager";
import Localization from "../core/Localization";
import { useWidth, WidthProps } from "../core/Responsive";
import Sys from "../core/Sys";
import TrackableCollection from "../core/TrackableCollection";
import { BASE_FONT } from "../main";
import PaneRow from "../models/PaneRow";
import { AccessLevel } from "../mustangui/AccessLevel";
import { GridNewRowChip } from "../mustangui/GridNewRowChip";
import { ResponsiveGridContext } from "../mustangui/ResponsiveGridContext";
import {
  Toolbar as ToolbarBase,
  Props as ToolbarChildProps,
} from "../mustangui/Toolbar";
import { FileInfo, ProgressStatus } from "../stores/DocumentStore";
import ErrorsStore from "../stores/ErrorsStore";
import { ThemeProvider } from "../theme";
import ComboBoxOption from "./ComboBoxOption";
import { ErrorMessage } from "./ErrorMessage";
import FocusTracker from "./FocusTracker";
import Icon from "./Icon";
import KeyboardNavigationGroup, {
  KeyboardNavigationGroup as KeyboardNavigationGroupBase,
} from "./KeyboardNavigationGroup";
import Presentation from "./Presentation";
import { RowErrorBadge } from "./RowErrorBadge";
import { FilterProvider, standardizeFilterValue } from "./table/FilterContext";
import { NoPrint } from "./table/NoPrint";
import { NoPrintFlex } from "./table/NoPrintFlex";
import { PaginationProvider } from "./table/PaginationContext";
import SelectionHeader from "./table/SelectionHeader";
import SelectionRenderer from "./table/SelectionRenderer";
import { TableProvider } from "./table/TableContext";
import Typography from "./Typography";

export interface TableProps {
  "aria-label"?: string;
  getAccessibleDescription?: (rowCount: number) => string;
  cardDepth: number;
  cellEdit?: boolean;
  columns: ColDef[];
  contentDataId: string;
  dataId: string;
  disableScrollOnPageChange?: boolean;
  disableSelectAll?: boolean;
  dropAreaChild?: React.ReactNode;
  externalFilter?: (row: PaneRow, filterValue: string) => boolean;
  footerToolbarChild?: object;
  getSelectedRowKeys?: () => string[];
  headerToolbarChild?: object;
  hideEmptyDocument?: boolean;
  ignoreBusinessErrors?: boolean;
  ignoreFilteredFromSelectedCount?: boolean;
  initialPageSize?: { [key in Breakpoint]: number };
  isColumnFlex: (colId: string) => boolean;
  isColumnVisible: (colId: string, breakpoint: string) => boolean;
  isDocumentGrid?: boolean;
  isInDialog?: true;
  keepHeaderOnSelect?: boolean;
  minRowHeight: number;
  name: string;
  onAllSelected?: (selected: boolean, rows: string[]) => void;
  onRowSelected?: (rowKey: string) => void;
  onRowUnselected?: (rowKey: string) => void;
  propagated: TableChildProps;
  resetPageOnPopulate?: boolean;
  rowSelection?: "multiple" | "single";
  selectToolbarChild?: object;
  setPopulate: (populate: ((rows: PaneRow[]) => void) | null) => void;
  setRestoreLostFocus?: (restoreLostFocus: (() => void) | null) => void;
  setScrollToRow?: (scrollToRow: ((rowKey: string) => void) | null) => void;
  showNoData?: boolean;
  summaryToolbarChild?: object;
  uploadFiles?: FileInfo[];
}

interface Props extends TableProps {
  tableKey: string;
}

interface InjectedProps {
  tableHasFocus: boolean;
  setTableHasFocus: (hasFocus: boolean) => void;
}

interface State {
  accessibleDescription?: string;
  currentRowKey?: string;
  filterValue: string | null;
  isEmpty?: boolean;
  isFooterToolbarFocused?: boolean;
  isGridReady?: boolean;
  isHeaderToolbarFocused?: boolean;
  isSummaryToolbarFocused?: boolean;
  pagination: {
    currentPageIndex: number;
    firstRowIndex: number;
    lastRowIndex: number;
    pageSize: number | null;
    totalPages: number;
    totalRows: number;
  };
  selectionToolbarVisible: boolean;
  visibleRows: PaneRow[];
}

interface ParentTableChildProps {
  cardDepth: number;
  columns: object[];
  configProps: {
    contentDataId: string;
    data?: object;
    dataId: string;
    name: string;
    tableKey: string;
  };
  getRelationshipComboBoxOptions: (
    widgetName: string,
    selectedOption: ComboBoxOption,
  ) => ComboBoxOption[];
  hasRelatedEditDialog?: boolean;
  isDocumentGrid: boolean;
  isProjectGrid: boolean;
  openRowEditDialog: (
    rowKey: string,
    isFirstOpenOfNewRow: boolean,
    restoreFocusElement: HTMLElement | null,
    appServerStateOnOpen: AppServerState | null,
  ) => void;
  populateData: () => void;
  uploadFiles: (files: FileList) => Promise<void>;
  validExtensions: string[] | null;
}

export interface SelectionContext {
  selectedCount: number;
}

export const selectionContext = React.createContext<SelectionContext>({
  selectedCount: 0,
});

export interface TableVerticalLayoutProps {
  parentTable: ParentTableChildProps;
  rowKey: string;
}

export interface TableChildProps {
  data?: object;
  parentRowKey?: string;
  parentTable: ParentTableChildProps & {
    description: string;
    focusHeaderToolbar: () => void;
    focusNewRow: () => void;
    getApi: () => GridApi<PaneRow>;
    getTable: () => Table;
    goToFirstPage: () => void;
    isVerticalLayout: boolean;
    scrollToRow: (rowKey: string) => void;
    selection: {
      getSelectedCount: () => number;
      getSelectedRows: () => PaneRow[];
      isMultiSelect: boolean;
      isRowSelected: (row: IRowNode<PaneRow>) => boolean;
      isSelectAllEnabled: boolean;
      selectionToolbarDisabled: () => boolean;
      setAllSelected: (selected: boolean) => void;
      setRowsSelected: (rows: IRowNode<PaneRow>[], selected: boolean) => void;
    };
    setStopEditingWhenGridLosesFocus: (stopEditing: boolean) => void;
  };
}

export interface TableContext {
  getGridApi: () => GridApi;
  hasHorizontalScrollBar: () => boolean;
}

interface WidgetProperties {
  accessLevel: AccessLevel;
  businessErrors: string[];
}

const getFocusStyles = (focused: boolean, theme: any) => ({
  borderBottomColor: focused
    ? `${theme.palette.grey[800]} !important`
    : undefined,
  borderBottomWidth: focused ? "2px !important" : undefined,
  borderBottomStyle: focused ? ("solid" as const) : undefined,
  borderTopColor: focused ? `${theme.palette.grey[800]} !important` : undefined,
  borderTopWidth: focused ? "2px !important" : undefined,
  borderTopStyle: focused ? ("solid" as const) : undefined,
});

const ErrorSummary = styled(Typography)(({ theme }) => ({
  color: theme.palette.danger.main,
  display: "grid",
  rowGap: "4px",
}));

const ToolbarHeaderWrapper = styled("div")(() => ({
  display: "flex",
  flex: "1",
  alignItems: "center",
  flexDirection: "row",
}));

const ToolbarDivider = styled("div")(({ theme }) => ({
  flexBasis: "auto",
  borderLeft: `1px solid ${theme.palette.divider}`,
  height: 50,
}));

const ToolbarSelectionWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "selectionToolbarVisible",
})<{ selectionToolbarVisible: boolean }>(
  ({ selectionToolbarVisible, theme }) => ({
    display: "flex",
    flex: selectionToolbarVisible ? 1 : undefined,
    alignItems: "center",
    flexDirection: "row",
  }),
);

const ToolbarsContainer = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "selectionToolbarVisible" && prop !== "focused",
})<{ selectionToolbarVisible: boolean; focused: boolean }>(
  ({ selectionToolbarVisible, focused = false, theme }) => ({
    display: "flex",
    backgroundColor: selectionToolbarVisible
      ? theme.palette.grey[200]
      : "inherit",
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
    ...getFocusStyles(focused, theme),
  }),
);

const ToolbarFooterWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "focused",
})<{ focused: boolean }>(({ focused = false, theme }) => ({
  ...getFocusStyles(focused, theme),
}));

export class Table extends React.Component<
  Props & InjectedProps & WidthProps,
  State
> {
  private static readonly ALL_PAGE_SIZE: number = 999999;
  private readonly accessibleDescriptionId: string;
  private agGridRef = React.createRef<HTMLDivElement>();
  private readonly cardDepth: number;
  private columnResizedTimeout: number;
  private componentDidUpdateTimeout: number;
  private readonly componentId: string;
  private readonly contentDataId: string;
  private readonly dataId: string;
  private readonly errorSummaryId: string;
  private readonly externalFilter:
    | ((row: PaneRow, filterValue: string) => boolean)
    | null;
  private filterValue: string | null = null;
  private focusOnPopulate: boolean = false;
  private footerToolbar: React.ReactNode | null = null;
  private footerToolbarAriaLabel: string | undefined = undefined;
  private readonly getAccessibleDescription?: (rowCount: number) => string;
  private readonly getSelectedRowKeys?: () => string[];
  private gridContainerRef = React.createRef<HTMLDivElement>();
  private gridSizeChangedTimeout: number;
  private hadSelected: boolean = false;
  private headerToolbar: React.ReactNode | null = null;
  private headerToolbarAriaLabel: string | undefined = undefined;
  private headerToolbarRef: React.RefObject<HTMLDivElement>;
  private readonly ignoreBusinessErrors?: boolean;
  private isKeyboardNavigatingCells = false;
  private lastRowErrors: string | null = null;
  private lastRowsData: string = "[]";
  private lastUploadFilesCount = 0;
  private readonly name: string;
  private noData: HTMLDivElement | null = null;
  private readonly onRowSelected?: (rowKey: string) => void;
  private readonly onRowUnselected?: (rowKey: string) => void;
  private readonly resetPageOnPopulate?: boolean;
  private readonly showNoData?: boolean;
  private populateDataTimeout: number;
  private populateDataWaitRenderTimeout: number;
  // Dictionary of upload progress components, keyed by upload file.
  private progressBars = new Map<object, HTMLElement>();
  private removeRowErrorsTimeout: number;
  // Dictionary of row error components, keyed by upload file.
  private rowErrors = new Map<object, HTMLElement>();
  private readonly rowSelection?: "multiple" | "single";
  private selectedRows = new Set<string>();
  private selectToolbar: React.ReactNode | null = null;
  private selectToolbarAriaLabel: string | undefined = undefined;
  private summaryToolbar: React.ReactNode | null = null;
  private tableRef: React.RefObject<HTMLDivElement>;
  private uploadFilesDisposer: IReactionDisposer;
  protected agProps: AgGridReactProps<PaneRow>;
  protected gridWidth: number;
  protected lastSelectedRows: PaneRow[] | null = null;
  protected trackableCollection: TrackableCollection;
  public gridApi: GridApi<PaneRow>;
  // Dictionary of row error messages, keyed by rowKey.
  public rowErrorMessages = new Map<string, string>();
  public rowErrorBadges = new Map<string, RowErrorBadge>();
  public stopEditingWhenGridLosesFocus = true;

  public constructor(props: Props & InjectedProps & WidthProps) {
    super(props);

    this.componentId = `table-${Sys.nextId}`;
    this.accessibleDescriptionId = `${this.componentId}-accessible-description`;
    this.errorSummaryId = `${this.componentId}-error-summary`;

    this.state = {
      filterValue: null,
      isEmpty: true,
      isFooterToolbarFocused: false,
      isHeaderToolbarFocused: false,
      isSummaryToolbarFocused: false,
      pagination: {
        currentPageIndex: 0,
        firstRowIndex: 0,
        lastRowIndex: 0,
        pageSize: null,
        totalPages: 0,
        totalRows: 0,
      },
      selectionToolbarVisible: false,
      visibleRows: [],
    };

    if (props.tableHasFocus) {
      this.focusOnPopulate = true;
    }

    props.setPopulate((r) => this.populateData(r));

    if (props.uploadFiles) {
      this.uploadFilesDisposer = autorun(() => {
        const uploadFiles: FileInfo[] = props.uploadFiles!;
        for (const fileInfo of uploadFiles) {
          this.uploadProgress(fileInfo.file, fileInfo.status, fileInfo.event);
        }

        if (uploadFiles.length > this.lastUploadFilesCount) {
          const newestFile: FileInfo = uploadFiles[uploadFiles.length - 1];
          this.scrollToRow(newestFile.file["rowKey"]);
        }
        this.lastUploadFilesCount = uploadFiles.length;
      });
    }

    if (props.setRestoreLostFocus) {
      props.setRestoreLostFocus(() => this.restoreLostFocus());
    }

    if (props.setScrollToRow) {
      props.setScrollToRow((rowKey) => this.scrollToRow(rowKey));
    }

    props.propagated.parentTable = {
      ...props.propagated.parentTable,
      focusHeaderToolbar: this.focusHeaderToolbar,
      focusNewRow: this.focusNewRow,
      getApi: () => this.gridApi,
      getTable: () => this,
      goToFirstPage: () => {
        throw new Error("goToFirstPage not implemented");
      },
      isVerticalLayout: false,
      scrollToRow: (rowKey) => this.scrollToRow(rowKey),
      selection: {
        getSelectedCount: () => this.getSelectedCount(),
        getSelectedRows: () => this.getSelectedRows(),
        isMultiSelect: props.rowSelection === "multiple",
        isRowSelected: (r) => this.isRowSelected(r),
        isSelectAllEnabled:
          props.rowSelection === "multiple" && !props.disableSelectAll,
        selectionToolbarDisabled: () => this.selectedRows.size > 0,
        setAllSelected: (s) => this.setAllSelected(s, true),
        setRowsSelected: (r, s) => this.setRowsSelected(r, s, true),
      },
      setStopEditingWhenGridLosesFocus: (s) =>
        this.setStopEditingWhenGridLosesFocus(s),
    };
    props.propagated.parentTable.configProps.tableKey = props.tableKey;

    // Store references to props that are used in the context of loading data.
    // This is required to avoid a MobX error warning that the props are being
    // accessed from within a reactive context.
    this.cardDepth = props.propagated.parentTable.cardDepth;
    this.contentDataId = props.contentDataId;
    this.dataId = props.dataId;
    this.externalFilter = props.externalFilter || null;
    this.getAccessibleDescription = props.getAccessibleDescription;
    this.getSelectedRowKeys = props.getSelectedRowKeys;
    this.ignoreBusinessErrors = props.ignoreBusinessErrors;
    this.name = props.name;
    this.onRowSelected = props.onRowSelected;
    this.onRowUnselected = props.onRowUnselected;
    this.resetPageOnPopulate = props.resetPageOnPopulate;
    this.rowSelection = props.rowSelection;
    this.showNoData = props.showNoData;

    this.agProps = {};

    this.agProps.animateRows = true;
    this.agProps.cacheQuickFilter = false;
    this.agProps.columnDefs = [];
    this.agProps.rowHeight = props.minRowHeight;
    this.agProps.context = {
      getGridApi: () => this.gridApi,
      hasHorizontalScrollBar: () => {
        const body: Element =
          this.gridContainerRef.current!.querySelector(".ag-body-viewport")!;

        return body.clientWidth < body.scrollWidth;
      },
    } as TableContext;

    let hasRowEdit: boolean = false;

    if (props.columns.length) {
      for (const column of props.columns) {
        column.cellEditorParams = {
          ...column.cellEditorParams,
          propagated: props.propagated,
        };
        column.cellRendererParams = {
          ...column.cellRendererParams,
          propagated: props.propagated,
        };

        if (column.colId === "_rowEdit") {
          hasRowEdit = true;
        } else {
          this.agProps.columnDefs.push(column);
        }
      }

      // Add flex column to fill remaining space.
      this.agProps.columnDefs.push({
        cellClass: hasRowEdit ? "cx-cell" : "cx-cell cx-cell-last",
        colId: "_filler",
        headerClass: hasRowEdit
          ? "cx-header-filler"
          : "cx-header-filler cx-header-last",
        headerComponent: () => (
          <div
            aria-label={Localization.getBuiltInMessage(
              "DataTable.emptyColumnHeaderLabel",
            )}
          ></div>
        ),
        hide: true,
        minWidth: 1,
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        suppressSizeToFit: false,
        width: 1,
      });

      if (hasRowEdit) {
        this.agProps.columnDefs.push(props.columns[props.columns.length - 1]);
      }
    }

    // FUTURE
    // Delta row data mode has performance benefits; however, this requires the
    // data store to be immutable which causes problems with how updates are
    // handled which presume in-place mutable data.
    // this.agProps.deltaRowDataMode = true;
    this.agProps.domLayout = "autoHeight";
    // https://www.ag-grid.com/javascript-grid-accessibility/#dom-order
    this.agProps.ensureDomOrder = true;
    this.agProps.getRowId = (params: GetRowIdParams<PaneRow>) => {
      return params.data.rowKey;
    };
    this.agProps.headerHeight = 48;

    this.agProps.defaultColDef = {
      suppressHeaderKeyboardEvent: (
        params: SuppressHeaderKeyboardEventParams,
      ) => {
        const tab = 9;

        return params.event.which === tab;
      },

      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        const tab = 9;

        return params.event.which === tab;
      },
    };

    this.agProps.pagination = true;
    // If paging is not configured we will display at most 999999 rows.
    this.agProps.paginationPageSize = Table.ALL_PAGE_SIZE;
    this.agProps.rowClassRules = {
      "ag-row-selected": (params: RowClassParams) => {
        return this.isRowSelected(params.node);
      },
      "cx-has-row-error": (params: RowClassParams) => {
        return this.rowErrorMessages.has(params.data?.rowKey);
      },
    };
    this.agProps.singleClickEdit = props.cellEdit;
    this.agProps.sortingOrder = ["asc", "desc"];
    this.agProps.stopEditingWhenCellsLoseFocus = false;
    this.agProps.suppressClickEdit = !props.cellEdit;
    this.agProps.suppressColumnVirtualisation = true;
    this.agProps.suppressDragLeaveHidesColumns = true;
    this.agProps.loading = false;
    this.agProps.suppressNoRowsOverlay = true;
    this.agProps.suppressPaginationPanel = true;
    this.agProps.suppressRowHoverHighlight = true;
    this.agProps.unSortIcon = true;

    if (props.columns.length && props.rowSelection) {
      const params = { propagated: props.propagated };

      this.agProps.columnDefs = (
        [
          {
            cellRenderer: SelectionRenderer,
            cellRendererParams: params,
            cellStyle: {
              borderBottomColor: colors.grey[300],
            },
            colId: "_checkboxSelection",
            headerClass: "cx-selection-header",
            headerComponent: SelectionHeader,
            headerComponentParams: params,
            hide: true,
            lockPosition: true,
            resizable: false,
            suppressMovable: true,
            suppressNavigable: false,
            suppressSizeToFit: true,
            width: 72,
          },
        ] as ColDef[]
      ).concat(this.agProps.columnDefs!);

      switch (props.rowSelection) {
        case "single":
          this.agProps.rowSelection = {
            mode: "singleRow",
            checkboxes: false,
            enableClickSelection: false,
          };
          break;
        case "multiple":
          this.agProps.rowSelection = {
            mode: "multiRow",
            checkboxes: false,
            headerCheckbox: false,
            enableClickSelection: false,
            enableSelectionWithoutKeys: true,
          };
          break;
        default:
          this.agProps.rowSelection = undefined;
      }
    }

    this.agProps.doesExternalFilterPass = (node: RowNode<PaneRow>): boolean => {
      if (this.externalFilter === null) {
        throw new Error(
          "An external filter must be set if external filtering is enabled",
        );
      }

      if (this.filterValue === null) {
        return true;
      }

      return this.externalFilter(node.data!, this.filterValue);
    };

    this.agProps.isExternalFilterPresent = (): boolean => {
      return this.externalFilter !== null;
    };

    this.agProps.onBodyScroll = () => {
      // Fix for bug where focus is lost on horizontal scroll
      if (this.isKeyboardNavigatingCells) {
        this.restoreLostFocus();
      }
    };

    this.agProps.onCellFocused = (event: CellFocusedEvent) => {
      if (event.forceBrowserFocus) {
        this.isKeyboardNavigatingCells = true;
        setTimeout(() => (this.isKeyboardNavigatingCells = false));
      }

      if (event.rowIndex != null) {
        let currentRowKey: string | undefined;

        if (event.rowPinned !== "top") {
          currentRowKey = this.gridApi.getDisplayedRowAtIndex(event.rowIndex)
            ?.data?.rowKey;
        } else {
          currentRowKey = this.gridApi.getPinnedTopRow(event.rowIndex)?.data
            ?.rowKey;
        }

        if (currentRowKey !== this.state.currentRowKey) {
          const prev = this.state.currentRowKey;
          this.setState({ currentRowKey: currentRowKey }, () => {
            this.updateFocusedRowClass(prev, currentRowKey);
          });
        } else {
          this.setState({ currentRowKey: currentRowKey }, () => {
            this.updateFocusedRowClass(undefined, currentRowKey);
          });
        }
      }
    };

    this.agProps.onColumnResized = (event: ColumnResizedEvent) => {
      if (
        !event.column ||
        !event.column.getColDef().suppressSizeToFit ||
        !event.finished
      ) {
        return;
      }

      this.gridApi.sizeColumnsToFit(this.gridWidth);
    };

    this.agProps.onDragStopped = () => {
      this.gridApi.refreshCells({ force: true });
      this.gridApi.refreshHeader();
    };

    this.agProps.onFilterChanged = (event: FilterChangedEvent) => {
      const count: number = this.gridApi.getDisplayedRowCount();

      this.gridApi.refreshHeader();
      this.updateNoData(count);

      if (this.state.currentRowKey && this.toolbarsHaveFocus) {
        setTimeout(() => {
          this.updateFocusedRowClass(undefined, this.state.currentRowKey);
        }, 0);
      }

      if (!!this.props.getAccessibleDescription) {
        const accessibleDescription = this.props.getAccessibleDescription(
          this.gridApi.paginationGetRowCount(),
        );
        this.setState({ accessibleDescription });
      }

      const visibleRows: PaneRow[] = [];
      this.gridApi.forEachNodeAfterFilter((node: RowNode<PaneRow>) => {
        visibleRows.push(node.data!);
      });
      this.setState({
        visibleRows: visibleRows,
      });

      Sys.debounceMethod(
        () =>
          Sys.announce(
            Localization.getBuiltInMessage(
              count === 0
                ? "DataTable.gridFilterAlertZero"
                : count === 1
                  ? "DataTable.gridFilterAlertSingle"
                  : "DataTable.gridFilterAlertMultiple",
              { count },
            ),
          ),
        "DataTable.gridFilterAlert",
        1000,
      );
    };

    this.agProps.onGridReady = (event: GridReadyEvent) => {
      this.gridApi = event.api;

      this.gridApi.setGridAriaProperty(
        "label",
        this.props["aria-label"] || null,
      );
      this.createToolbars();

      this.setState({ isGridReady: true }, () => {
        this.updateClasses();

        this.gridApi.setGridAriaProperty(
          "describedBy",
          this.accessibleDescriptionId,
        );

        if (!this.props.isInDialog) {
          this.agGridElementByClass("ag-body-viewport").classList.add(
            "cx-body-viewport",
          );
          this.agGridElementByClass("ag-header-viewport").classList.add(
            "cx-header-container",
          );
          this.agGridElementByClass("ag-root").classList.add("cx-root");
        }

        // NOTE: This is a hack to fix a bug with AG-Grid
        // The bug is when you hit shift + tab 3 times, a 500 error occurs.
        // "TypeError: t is undefined".
        // Issue logged:  https://ag-grid.zendesk.com/hc/en-us/requests/35756
        // Item was added to the AG Grid Pipeline under AG-13739
        this.agGridElementByClass("ag-header-viewport").setAttribute(
          "tabindex",
          "-1",
        );
        this.agGridElementByClass("ag-center-cols-viewport").setAttribute(
          "tabindex",
          "-1",
        );
        this.agGridElementByClass("ag-sticky-top-viewport").setAttribute(
          "tabindex",
          "-1",
        );
        this.agGridElementByClass(
          "ag-body-horizontal-scroll-viewport",
        ).setAttribute("tabindex", "-1");
      });
    };

    this.agProps.onGridSizeChanged = (event: GridSizeChangedEvent) => {
      // FUTURE: Log a bug with ag-Grid
      // Event is sometimes fired after component is unmounted.
      if (!this.gridContainerRef.current) {
        return;
      }

      window.clearTimeout(this.gridSizeChangedTimeout);
      this.gridSizeChangedTimeout = window.setTimeout(() => {
        // updateLayout() mutates this.gridWidth
        const currentWidth = this.gridWidth;
        // Chrome sometimes is off by 1px due to OS scaling
        const newWidth = event.clientWidth - 1;
        this.updateLayout(event.clientHeight, newWidth);
        // The grid filter must be reevaluated after the layout is updated to
        // ensure the column visibility at the new width is current.
        if (currentWidth !== newWidth) {
          this.reevaluateGridFilter();
        }
      }, 100);
    };

    this.agProps.onPaginationChanged = (event: PaginationChangedEvent) => {
      if (event.newPage) {
        // Fix for bug where focus is lost on page change
        this.restoreLostFocus();
      }

      const pageSize = event.api.paginationGetPageSize();
      this.setState({
        pagination: {
          currentPageIndex: event.api.paginationGetCurrentPage(),
          firstRowIndex: event.api.getFirstDisplayedRowIndex(),
          lastRowIndex: event.api.getLastDisplayedRowIndex(),
          pageSize: pageSize === Table.ALL_PAGE_SIZE ? null : pageSize,
          totalPages: event.api.paginationGetTotalPages(),
          totalRows: event.api.paginationGetRowCount(),
        },
      });
    };

    this.agProps.onSelectionChanged = (event: SelectionChangedEvent) => {
      this.updateSelection();
    };

    this.agProps.onSortChanged = () => {
      if (this.state.currentRowKey && this.props.tableHasFocus) {
        setTimeout(() => {
          this.updateFocusedRowClass(undefined, this.state.currentRowKey);
        }, 0);
      }
    };

    this.headerToolbarRef = React.createRef<HTMLDivElement>();
    this.tableRef = React.createRef<HTMLDivElement>();
  }

  private addNewIndicator() {
    const pinnedTop = this.agGridElementByClass("ag-floating-top");
    const count: number = this.gridApi.getPinnedTopRowCount();

    if (count && !pinnedTop.querySelector(".cx-new-container")) {
      const container: HTMLDivElement = document.createElement("div");
      container.className = "cx-new-container";
      pinnedTop.appendChild(container);

      ReactDOM.render(
        <ThemeProvider>
          <div className="cx-new-container-border" />
          <GridNewRowChip />
        </ThemeProvider>,
        container,
      );
    }
  }

  private addProgressBar(file: File) {
    const pinnedTop = this.gridContainerRef.current!.querySelector(
      ".cx-floating-top",
    )! as HTMLElement;
    const container: HTMLDivElement = document.createElement("div");
    container.className = "cx-progress-container";
    pinnedTop.appendChild(container);

    ReactDOM.render(
      <ThemeProvider>
        <div className="cx-progress-bar" id={file["rowKey"]}>
          <div className="cx-progress" style={{ width: 0 }} />
          <Icon
            className="cx-progress-complete"
            icon="far fa-check-circle"
            style={{ height: 14 }}
          />
        </div>
      </ThemeProvider>,
      container,
      () => {
        const progressBar = container.querySelector(
          `[id='${file["rowKey"]}']`,
        ) as HTMLElement;

        this.positionProgressBar(progressBar);
        this.progressBars.set(file, progressBar);
      },
    );
  }

  private addRowError(rowNode: IRowNode, messages: string[]) {
    const container: HTMLDivElement = document.createElement("div");
    const rowKey = rowNode.data.rowKey;

    if (rowNode.data.isNew) {
      container.className = "cx-row-error-pinned-top-container";
      const pinnedTop = this.agGridElementByClass("ag-floating-top");
      pinnedTop.appendChild(container);
    } else {
      container.className = "cx-row-error-container";
      const centerColumns = this.agGridElementByClass("ag-body-viewport");
      centerColumns.appendChild(container);
    }

    const rowMessages = [...messages];
    rowMessages.forEach((message: string, index: number) => {
      if (!message.endsWith(".")) {
        rowMessages[index] = `${message}. `;
      }
    });

    // Set error messages BEFORE rendering so rowClassRules can evaluate correctly
    this.rowErrorMessages.set(rowKey, rowMessages.join(" "));

    ReactDOM.render(
      <ThemeProvider>
        <div className="cx-row-error" id={rowKey}>
          <RowErrorBadge
            errors={messages}
            propagated={this.props.propagated}
            rowKey={rowKey}
          />
        </div>
      </ThemeProvider>,
      container,
      () => {
        const rowError = container.querySelector(
          `[id='${rowKey}']`,
        ) as HTMLElement;

        this.rowErrors.set(rowKey, rowError);

        // Position badge at correct row height
        this.positionRowErrorBadge(rowError, rowNode);

        // Trigger row redraw to apply cx-has-row-error class
        this.gridApi.redrawRows({ rowNodes: [rowNode] });
      },
    );
  }

  private agGridElementByClass(className: string) {
    return this.agGridRef.current?.getElementsByClassName(className)[0]!;
  }

  private createToolbars() {
    const propagatedProps: TableChildProps = this.props.propagated;

    if (this.props.headerToolbarChild) {
      this.headerToolbar = Presentation.create(
        this.props.headerToolbarChild,
        propagatedProps,
      );
      this.headerToolbarAriaLabel = Localization.getBuiltInMessage(
        "DataTable.toolbarLabel",
        {
          description: this.props.propagated.parentTable.description,
        },
      );
    }

    if (this.props.selectToolbarChild && this.props.rowSelection) {
      this.selectToolbar = Presentation.create(
        this.props.selectToolbarChild,
        propagatedProps,
      );
      this.selectToolbarAriaLabel = Localization.getBuiltInMessage(
        "DataTable.selectionToolbarLabel",
        {
          description: this.props.propagated.parentTable.description,
        },
      );
    }

    if (this.props.footerToolbarChild) {
      this.footerToolbar = Presentation.create(
        this.props.footerToolbarChild,
        propagatedProps,
      );
      this.footerToolbarAriaLabel = Localization.getBuiltInMessage(
        "DataTable.footerToolbarLabel",
        {
          description: this.props.propagated.parentTable.description,
        },
      );
    }

    if (this.props.summaryToolbarChild) {
      this.summaryToolbar = Presentation.create(
        this.props.summaryToolbarChild,
        propagatedProps,
      );
    }
  }

  private getSelectedCount() {
    if (this.props.ignoreFilteredFromSelectedCount) {
      let count = 0;
      this.gridApi.forEachNodeAfterFilter((node) => {
        if (this.selectedRows.has(node.data!.rowKey)) {
          count++;
        }
      });
      return count;
    }
    return this.selectedRows.size;
  }

  private getSelectedRows(): PaneRow[] {
    if (!this.gridApi) {
      throw "Grid is not ready";
    }

    const rows: PaneRow[] = [];
    this.gridApi.forEachNode((node) => {
      if (this.selectedRows.has(node!.data!.rowKey)) {
        rows.push(node!.data!);
      }
    });

    for (let i = 0; i < this.gridApi.getPinnedTopRowCount(); i++) {
      const row: IRowNode = this.gridApi.getPinnedTopRow(i)!;
      if (this.selectedRows.has(row.data.rowKey)) {
        rows.push(row.data);
      }
    }

    return rows;
  }

  private getTableErrorDetails(): string[] {
    const result: string[] = [];

    if (this.gridApi) {
      ErrorsStore.getTableWidgetErrors(this.props.contentDataId).forEach(
        (businessErrorWidget) => {
          const rowNode = this.gridApi.getRowNode(businessErrorWidget.rowKey);

          if (rowNode) {
            const columnDescription: string | null =
              this.props.propagated.parentTable.columns.find(
                (column) => column["name"] === businessErrorWidget.widgetName,
              )!["header"];
            const rowNumber: number = rowNode.rowIndex!;

            result.push(
              Localization.getBuiltInMessage("DataTable.cellError", {
                columnDescription,
                rowNumber,
              }),
            );
          }
        },
      );

      ErrorsStore.getTableRowErrors(this.props.contentDataId).forEach(
        (messages, rowKey) => {
          let rowNode = this.gridApi.getRowNode(rowKey);

          if (!rowNode) {
            for (
              let index = 0;
              index < this.gridApi.getPinnedTopRowCount();
              index++
            ) {
              const pinnedRow: IRowNode = this.gridApi.getPinnedTopRow(index)!;

              if (pinnedRow.data.rowKey === rowKey) {
                rowNode = pinnedRow;
                break;
              }
            }
          }

          if (rowNode) {
            const rowNumber: number = rowNode.rowIndex!;

            result.push(
              ...messages.map((errorMessage) =>
                Localization.getBuiltInMessage("DataTable.rowError", {
                  errorMessage,
                  rowNumber,
                }),
              ),
            );
          }
        },
      );

      result.forEach((message: string, index: number) => {
        if (!message.endsWith(".")) {
          result[index] = `${message}. `;
        }
      });
    }

    return result;
  }

  private getTableErrors(): string[] {
    // Required because businessErrors are not provided for all tables.
    if (this.ignoreBusinessErrors) {
      return [];
    }

    const row = PaneRow.get(this.dataId);
    const widget = row!.getWidgetT<null, WidgetProperties>(this.name);
    return widget.properties.businessErrors;
  }

  private goToNextPage = (): void => {
    if (!this.gridApi) {
      throw new Error("Grid API is not initialized");
    }

    this.gridApi.paginationGoToNextPage();
  };

  private goToPreviousPage = (): void => {
    if (!this.gridApi) {
      throw new Error("Grid API is not initialized");
    }

    this.gridApi.paginationGoToPreviousPage();
  };

  private initializePagination = (): void => {
    if (!this.gridApi) {
      throw new Error("Grid API is not initialized");
    }

    const initialPageSizes = this.props.initialPageSize || {
      lg: 10,
      md: 10,
      sm: 5,
      xl: 10,
      xs: 1,
    };

    this.gridApi.setGridOption(
      "paginationPageSize",
      initialPageSizes[this.props.width],
    );
  };

  private isRowSelected(row: IRowNode<PaneRow>) {
    return this.selectedRows.has(row.data!.rowKey);
  }

  private onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      !event.currentTarget.contains(event.relatedTarget as Node) &&
      event.target instanceof HTMLElement &&
      event.currentTarget.contains(event.target as Node)
    ) {
      if (this.stopEditingWhenGridLosesFocus) {
        this.gridApi.stopEditing();
      }

      if (event.relatedTarget !== null) {
        // Only reset the flag if another element is gaining focus. No element
        // gaining focus indicates an unmount, at which point we want to ensure
        // the flag remains set so that focus is restored to the card control
        // that gets remounted. If both are getting unmounted, the entire
        // responsive grid context will be removed, which will effectively clear
        // the flag anyway.
        this.props.setTableHasFocus(false);
      }

      if (!this.toolbarsHaveFocus && this.state.currentRowKey) {
        this.updateFocusedRowClass(this.state.currentRowKey, undefined);
      }
    }
  };

  private onFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      !event.currentTarget.contains(event.relatedTarget as Node) &&
      event.currentTarget.contains(event.target as Node)
    ) {
      this.props.setTableHasFocus(true);
    }
  };

  private onFocusChangedHeader = (isHeaderToolbarFocused: boolean): void => {
    this.setState({ isHeaderToolbarFocused }, () => {
      if (isHeaderToolbarFocused && this.state.currentRowKey) {
        // Check if new rows are being added
        const pinnedRowCount = this.gridApi?.getPinnedTopRowCount() || 0;
        if (pinnedRowCount > 0) {
          this.updateFocusedRowClass(this.state.currentRowKey, undefined);
          this.setState({ currentRowKey: undefined });
        } else {
          this.updateFocusedRowClass(undefined, this.state.currentRowKey);
        }
      } else if (!this.toolbarsHaveFocus && this.state.currentRowKey) {
        this.updateFocusedRowClass(this.state.currentRowKey, undefined);
      }
    });
  };

  private onFocusChangedFooter = (isFooterToolbarFocused: boolean): void => {
    this.setState({ isFooterToolbarFocused }, () => {
      if (isFooterToolbarFocused && this.state.currentRowKey) {
        this.updateFocusedRowClass(undefined, this.state.currentRowKey);
      } else if (!this.toolbarsHaveFocus && this.state.currentRowKey) {
        this.updateFocusedRowClass(this.state.currentRowKey, undefined);
      }
    });
  };

  private onFocusChangedSummary = (isSummaryToolbarFocused: boolean): void => {
    this.setState({ isSummaryToolbarFocused }, () => {
      if (isSummaryToolbarFocused && this.state.currentRowKey) {
        this.updateFocusedRowClass(undefined, this.state.currentRowKey);
      } else if (!this.toolbarsHaveFocus && this.state.currentRowKey) {
        this.updateFocusedRowClass(this.state.currentRowKey, undefined);
      }
    });
  };

  private onFocusTabbableElement = () => {
    this.focus();
  };

  private onWindowFocus = () => {
    if (this.props.tableHasFocus && this.state.currentRowKey) {
      this.updateFocusedRowClass(undefined, this.state.currentRowKey);
    }
  };

  private onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === " " && this.gridApi.getEditingCells().length <= 0) {
      event.preventDefault();

      const cell = this.gridApi.getFocusedCell();

      if (cell) {
        this.gridApi.setFocusedCell(
          cell.rowIndex,
          cell.column,
          cell.rowPinned || undefined,
        );
      }
    }

    if (
      (event.key === "ArrowLeft" ||
        event.key === "ArrowRight" ||
        event.key === "ArrowUp" ||
        event.key === "ArrowDown") &&
      this.gridApi.getEditingCells().length <= 0 &&
      this.rowErrorBadges.size > 0
    ) {
      const cell = this.gridApi.getFocusedCell();
      const columns = this.gridApi
        .getDisplayedCenterColumns()
        .filter((c) => c.getColId() !== "_filler");

      this.rowErrorBadges.forEach((rowErrorBadge: RowErrorBadge) =>
        rowErrorBadge.close(),
      );

      if (
        !!columns.length &&
        columns[columns.length - 1].getColId() === cell?.column.getColId()
      ) {
        let rowKey: string | undefined = undefined;

        if (cell.rowPinned === "top") {
          rowKey = this.gridApi.getPinnedTopRow(cell.rowIndex)?.data?.rowKey;
        } else {
          rowKey = this.gridApi.getDisplayedRowAtIndex(cell.rowIndex)?.data
            ?.rowKey;
        }

        if (rowKey) {
          this.rowErrorBadges.get(rowKey)?.open();
        }
      }
    }
  };

  private populateData(data: PaneRow[]) {
    const errorCount: number = ErrorsStore.getTableErrorCount(
      this.contentDataId,
    );
    const tableErrors: string[] = this.getTableErrors();

    window.clearTimeout(this.populateDataWaitRenderTimeout);

    // Always access the data to trigger any mobx observers
    const dataSerialized = JSON.stringify(toJS(data));

    if (!this.gridApi) {
      // Wait until rendering is complete to make sure any paging widget
      // is rendered
      this.populateDataWaitRenderTimeout = window.setTimeout(() =>
        this.populateData(data),
      );

      return;
    }

    if (this.showNoData) {
      this.updateNoData(data.length);
    }

    if (this.resetPageOnPopulate) {
      this.gridApi.paginationGoToFirstPage();
    }

    if (!!this.getAccessibleDescription) {
      const accessibleDescription = this.getAccessibleDescription(data.length);
      this.setState({ accessibleDescription });
    }

    // Only populate if the data has changed since the last time
    // we accessed it
    if (this.lastRowsData === dataSerialized) {
      return;
    }

    this.lastRowsData = dataSerialized;

    this.setState({ isEmpty: data.length === 0 });

    if (!!errorCount || tableErrors.length > 0) {
      this.gridApi.setGridAriaProperty(
        "describedby",
        `${this.accessibleDescriptionId} ${this.errorSummaryId}`,
      );
      this.gridApi.setGridAriaProperty("invalid", "true");
      this.gridApi.setGridAriaProperty("errormessage", this.errorSummaryId);
    } else {
      this.gridApi.setGridAriaProperty(
        "describedby",
        this.accessibleDescriptionId,
      );
      this.gridApi.setGridAriaProperty("invalid", "false");
      this.gridApi.setGridAriaProperty("errormessage", "");
    }

    if (this.getSelectedRows().length > 0) {
      this.lastSelectedRows = this.getSelectedRows();
    }

    this.gridApi.setGridOption(
      "rowData",
      data.filter((value) => !value.isNew),
    );
    this.gridApi.setGridOption(
      "pinnedTopRowData",
      data.filter((value) => value.isNew),
    );

    if (this.showNoData) {
      this.updateNoData(data.length);
    }

    if (this.getSelectedRowKeys !== undefined) {
      this.selectedRows.clear();
      for (const rowId of this.getSelectedRowKeys()) {
        this.selectedRows.add(rowId);
      }
    } else {
      // Prune the selected state for any deleted rows
      const rowKeys = new Set<string>(data.map<string>((i) => i["rowKey"]));
      for (const key of this.selectedRows) {
        if (!rowKeys.has(key)) {
          this.selectedRows.delete(key);
        }
      }
    }

    if (data.length > 0 && this.lastSelectedRows) {
      const rows: IRowNode<PaneRow>[] = [];
      this.gridApi.forEachNode((node: IRowNode<PaneRow>) => {
        if (
          this.lastSelectedRows!.some(
            (selected) => node.data!.rowKey === selected.rowKey,
          )
        ) {
          rows.push(node);
        }
      });

      for (let i = 0; i < this.gridApi.getPinnedTopRowCount(); i++) {
        const row: IRowNode<PaneRow> = this.gridApi.getPinnedTopRow(i)!;
        if (
          this.lastSelectedRows.some(
            (selected) => row.data!.rowKey === selected.rowKey,
          )
        ) {
          rows.push(row);
        }
      }
      this.setRowsSelected(rows, true, false);
    }
    this.lastSelectedRows = null;

    this.updateClasses();
    this.gridApi.refreshCells();

    // Must be deferred so it occurs after re-render.
    window.clearTimeout(this.populateDataTimeout);
    this.populateDataTimeout = window.setTimeout(() => {
      this.gridApi.refreshHeader();
      this.updateSelection();
      this.removeProgressBars();
      this.updateRowErrors();
      this.addNewIndicator();
    }, 10);

    this.setState({
      visibleRows: data,
    });

    if (this.focusOnPopulate) {
      this.focusOnPopulate = false;
      FocusManager.grabFocusForChild(
        this.gridContainerRef.current!,
        FocusManager.selectors.focusable,
      );
    }
  }

  private positionAllProgressBars() {
    this.progressBars.forEach((_progressBar: HTMLDivElement) => {
      this.positionProgressBar(_progressBar);
    });
  }

  private positionProgressBar(progressBar: HTMLElement) {
    for (let index = 0; index < this.gridApi.getPinnedTopRowCount(); index++) {
      const rowNode: IRowNode = this.gridApi.getPinnedTopRow(index)!;

      if (rowNode.data.rowKey === progressBar.id && rowNode.rowHeight) {
        progressBar.style.top = `${(index + 1) * rowNode.rowHeight - 6}px`;
        break;
      }
    }
  }

  private positionRowErrorBadge(rowError: HTMLElement, rowNode: IRowNode) {
    // Position badge vertically to align with the correct row
    if (rowNode.rowHeight) {
      if (rowNode.data.isNew) {
        // Pinned top row
        const index = rowNode.rowIndex || 0;
        rowError.style.top = `${index * rowNode.rowHeight}px`;
      } else {
        // Regular row
        rowError.style.top = `${(rowNode.rowIndex || 0) * rowNode.rowHeight}px`;
      }
      // Don't set height - badge positions itself at bottom right without taking up space
    }
  }

  private reevaluateGridFilter() {
    if (!this.state.filterValue) {
      return;
    }

    if (this.props.externalFilter) {
      this.gridApi.onFilterChanged();
      return;
    }

    const value = standardizeFilterValue(this.state.filterValue);
    // Must filter on a different value to force the filter to
    // reevaluate the value.  Using a value to exclude
    // all rows for a cleaner display.
    this.gridApi.setGridOption("quickFilterText", "$does_not_exist$");
    this.gridApi.setGridOption("quickFilterText", value);
  }

  private refreshCellRenderer(column: string, rows?: IRowNode[]) {
    this.gridApi
      .getCellRendererInstances({
        columns: [column],
        rowNodes: rows,
      })
      .forEach((cell) => cell.refresh({} as ICellRendererParams));
  }

  private removeProgressBars() {
    if (this.props.uploadFiles === undefined) {
      return;
    }

    const pinnedRowCount = this.gridApi.getPinnedTopRowCount();
    if (pinnedRowCount) {
      const filesToRemove = [];
      for (const [file, progressBar] of this.progressBars) {
        let documentRowExists: boolean = false;
        for (let index = 0; index < pinnedRowCount; index++) {
          if (
            this.gridApi.getPinnedTopRow(index)!.data.rowKey === progressBar.id
          ) {
            documentRowExists = true;
          }
        }

        if (!documentRowExists) {
          ReactDOM.unmountComponentAtNode(progressBar.parentElement!);

          const uploadFile: FileInfo | undefined = this.props.uploadFiles.find(
            (f) => f.file["rowKey"] === progressBar.id,
          );

          if (uploadFile) {
            const uploadFileIndex = this.props.uploadFiles.indexOf(uploadFile);
            this.props.uploadFiles.splice(uploadFileIndex, 1);
          }

          filesToRemove.push(file);
        }
      }

      for (const fileToRemove of filesToRemove) {
        this.progressBars.delete(fileToRemove);
      }

      if (filesToRemove.length > 0) {
        this.positionAllProgressBars();
      }
    } else {
      this.progressBars.forEach((progressBar: HTMLDivElement) => {
        ReactDOM.unmountComponentAtNode(progressBar.parentElement!);
      });

      this.progressBars.clear();
      this.props.uploadFiles.length = 0;
    }
  }

  private removeRowErrors() {
    const errorContainers: HTMLElement[] = [];

    this.rowErrors.forEach((rowError: HTMLDivElement) => {
      errorContainers.push(rowError.parentElement!);

      ReactDOM.unmountComponentAtNode(rowError.parentElement!);
    });

    if (errorContainers.length > 0) {
      window.clearTimeout(this.removeRowErrorsTimeout);
      this.removeRowErrorsTimeout = window.setTimeout(() => {
        errorContainers.forEach((errorContainer: HTMLDivElement) => {
          const centerColumns = this.agGridElementByClass("ag-body-viewport");
          const pinnedTop = this.agGridElementByClass("ag-floating-top");

          try {
            centerColumns.removeChild(errorContainer);
            pinnedTop.removeChild(errorContainer);
          } catch {
            // If the element does not exist anymore ignore it.
          }
        });
      });
    }

    this.rowErrors.clear();
  }

  private restoreLostFocus(): void {
    const cell = this.gridApi.getFocusedCell();
    if (cell) {
      this.gridApi.setFocusedCell(
        cell.rowIndex,
        cell.column,
        cell.rowPinned || undefined,
      );
    }
  }

  private scrollToRow(rowKey: string): void {
    let rowNode: IRowNode | null = null;
    let rowIndex = -1;
    for (let i = 0; i < this.gridApi.getPinnedTopRowCount(); i++) {
      rowNode = this.gridApi.getPinnedTopRow(i)!;
      if ((rowNode.data as PaneRow).rowKey === rowKey) {
        rowIndex = i;
        break;
      }
    }

    if (rowIndex < 0 || !rowNode) {
      throw new Error(`Row ${rowKey} not found`);
    }

    const newRowContainer: Element =
      this.gridContainerRef.current!.querySelector(
        ".ag-floating-top-container",
      )!;

    const containerOffset: number = newRowContainer.getBoundingClientRect().top;

    if (rowNode.rowHeight) {
      const rowBottomOffset: number =
        containerOffset + rowNode.rowHeight * (rowIndex + 1);
      const rowTopOffset: number =
        containerOffset + rowNode.rowHeight * rowIndex;

      if (rowBottomOffset + 10 > window.innerHeight) {
        window.scrollTo(
          0,
          window.scrollY + rowBottomOffset + 10 - window.innerHeight,
        );
      } else if (rowTopOffset < 10) {
        window.scrollTo(0, window.scrollY + rowTopOffset - 10);
      }
    }
  }

  private setAllSelected(selected: boolean, userInitiated: boolean) {
    if (this.props.rowSelection === "single") {
      throw "Cannot select more than one row when rowSelection is single";
    }

    const changedRows: string[] = [];

    // Use built-in row selection to update the row style. For pinned rows
    // force a full redraw to trigger the classRules.
    if (selected) {
      this.gridApi.forEachNodeAfterFilter((node) => {
        this.selectedRows.add(node.data!.rowKey);
        changedRows.push(node.data!.rowKey);
      });
      this.gridApi.selectAllFiltered();
    } else {
      this.gridApi.forEachNodeAfterFilter((node) => {
        this.selectedRows.delete(node.data!.rowKey);
        changedRows.push(node.data!.rowKey);
      });
      this.gridApi.deselectAllFiltered();
    }

    if (this.props.onAllSelected) {
      this.props.onAllSelected(selected, changedRows);
    }

    const pinnedRows = [];
    for (let i = 0; i < this.gridApi.getPinnedTopRowCount(); i++) {
      const row: IRowNode = this.gridApi.getPinnedTopRow(i)!;
      if (selected) {
        this.selectedRows.add(row.data["rowKey"]);
      } else {
        this.selectedRows.delete(row.data["rowKey"]);
      }
      pinnedRows.push(row);

      if (userInitiated) {
        if (selected && this.props.onRowSelected) {
          this.props.onRowSelected(row.data["rowKey"]);
        } else if (!selected && this.props.onRowUnselected) {
          this.props.onRowUnselected(row.data["rowKey"]);
        }
      }
    }

    // Force a redraw to trigger the classRules
    // This must be called for Select All even if there are no pinned rows, in
    // order to force a refresh (see B2C-14776).
    this.gridApi.redrawRows({ rowNodes: pinnedRows });

    this.refreshCellRenderer("_checkboxSelection");
    this.gridApi.refreshHeader();
  }

  private setColumns(): boolean {
    if (!this.gridApi || !this.props.columns) {
      return false;
    }

    const flexColumns: string[] = [];
    let columnsChanged = false;

    for (const column of this.props.columns) {
      const agColumn: Column | null = this.gridApi.getColumn<PaneRow>(
        column.colId!,
      );

      if (agColumn) {
        const wasVisible: boolean = agColumn.isVisible();
        const isVisible: boolean = this.props.isColumnVisible(
          column.colId!,
          this.props.width,
        );

        if (wasVisible !== isVisible) {
          this.gridApi.setColumnsVisible([column.colId!], isVisible);
          columnsChanged = true;
        }

        if (!column.width && isVisible && !wasVisible) {
          this.gridApi.autoSizeColumns([column.colId!]);

          // Remove extra space to the right of the header that
          // Ag-grid leaves for tools which we don't use
          const minWidth = agColumn.getMinWidth();
          let width = agColumn.getActualWidth();
          if (minWidth && width > minWidth) {
            width = Math.max(width * 0.89, minWidth);
            this.gridApi.setColumnWidths([{ key: agColumn, newWidth: width }]);
          }
        }

        if (isVisible && this.props.isColumnFlex(column.colId!)) {
          flexColumns.push(column.colId!);
        }
      }
    }

    if (columnsChanged) {
      // Column is initially hidden and displayed here so that the first
      // load of the grid is cleaner.
      this.gridApi.setColumnsVisible(["_checkboxSelection", "_rowEdit"], true);

      this.gridApi.setColumnsVisible(["_filler"], !flexColumns.length);
    }

    this.gridApi.sizeColumnsToFit(this.gridWidth);

    if (flexColumns.length) {
      this.gridApi.refreshCells({ columns: flexColumns, force: true });
    }

    return columnsChanged;
  }

  private setPageSize = (pageSize: number | null): void => {
    if (!this.gridApi) {
      throw new Error("Grid API is not initialized");
    }

    const focusedCell = this.gridApi.getFocusedCell();

    const size = pageSize === null ? Table.ALL_PAGE_SIZE : pageSize;
    this.gridApi.setGridOption("paginationPageSize", size);
    this.gridApi.paginationGoToFirstPage();

    if (
      pageSize !== null &&
      focusedCell &&
      focusedCell.rowIndex > pageSize - 1
    ) {
      this.gridApi.clearFocusedCell();
    }
  };

  private setFilterValue = (filterValue: string | null) => {
    this.setState({ filterValue: filterValue });

    if (this.props.externalFilter) {
      // The external filter is run in the context of the async data load,
      // which places it in a MobX reactive context. Accessing state directly
      // results in a warning from MobX, so a private member is used instead.
      this.filterValue = filterValue;
      this.gridApi.onFilterChanged();
      return;
    }

    if (filterValue === null) {
      this.gridApi.setGridOption("quickFilterText", "");
      return;
    }

    Sys.debounceMethod(
      () => {
        const value = standardizeFilterValue(filterValue);
        this.gridApi.setGridOption("quickFilterText", value);
      },
      "DataTable.setFilterValue",
      300,
    );
  };

  private setRowsSelected(
    rows: IRowNode[],
    selected: boolean,
    userInitiated: boolean,
  ) {
    if (this.rowSelection === "single") {
      if (rows.length > 1) {
        throw new Error(
          "Cannot select more than one row when rowSelection is single",
        );
      }

      if (selected && this.selectedRows.size > 0) {
        const rowId: string = this.selectedRows.values().next().value;
        if (userInitiated && this.onRowUnselected) {
          this.onRowUnselected(rowId);
        }

        const row = this.gridApi.getRowNode(rowId);
        if (row) {
          this.refreshCellRenderer("_checkboxSelection", [row]);
        }
        this.selectedRows.clear();
      }
    }

    const pinnedRows: IRowNode[] = [];
    for (const row of rows) {
      const rowKey = row.data.rowKey;
      if (selected) {
        this.selectedRows.add(rowKey);
      } else {
        this.selectedRows.delete(rowKey);
      }
      // Use built-in row selection to update the row style. For pinned rows
      // force a full redraw to trigger the classRules.
      if (row.isRowPinned()) {
        pinnedRows.push(row);
      } else {
        row.setSelected(selected);
      }

      if (userInitiated) {
        if (selected && this.onRowSelected) {
          this.onRowSelected(rowKey);
        } else if (!selected && this.onRowUnselected) {
          this.onRowUnselected(rowKey);
        }
      }
    }

    this.refreshCellRenderer("_checkboxSelection", rows);
    this.gridApi.refreshHeader();

    // Only redraw rows if there are pinned rows. Otherwise redrawing rows on
    // selection causes problems with VoiceOver (see B2C-14639).
    if (!!pinnedRows.length) {
      // redrawRows destroys/recreates the pinned-row DOM, losing browser focus
      // (see PPM-610). Capture the focused cell before the redraw and restore
      // it afterward, deferred so React has re-rendered the cell content.
      const focusedCell = this.gridApi.getFocusedCell();
      this.gridApi.redrawRows({ rowNodes: pinnedRows });
      this.gridApi.dispatchEvent({ type: "selectionChanged" });
      if (focusedCell) {
        window.setTimeout(() => {
          if (!this.gridApi) {
            return;
          }
          this.gridApi.setFocusedCell(
            focusedCell.rowIndex,
            focusedCell.column,
            focusedCell.rowPinned || undefined,
          );
        });
      }
    }
  }

  private setStopEditingWhenGridLosesFocus(stopEditing: boolean): void {
    this.stopEditingWhenGridLosesFocus = stopEditing;
  }

  private updateClasses() {
    if (!this.gridApi) {
      return;
    }

    const ariaDescriptionContainer = this.agGridElementByClass(
      "ag-aria-description-container",
    );
    if (ariaDescriptionContainer) {
      ariaDescriptionContainer.remove();
    }

    const body: Element = this.agGridElementByClass("ag-body-viewport");
    const header: Element = this.agGridElementByClass("ag-header");
    const pinnedTop: Element = this.agGridElementByClass("ag-floating-top");

    if (body && header) {
      if (pinnedTop) {
        if (this.gridApi.getPinnedTopRowCount()) {
          header.classList.add("cx-header");
          pinnedTop.classList.add("cx-floating-top");
        } else {
          header.classList.remove("cx-header");
          pinnedTop.classList.remove("cx-floating-top");
        }
      }
    }
  }

  // This function needs to be manually called by the different cases of focus/blur
  // since there is no way to trigger row refreshes without a full row redraw.
  private updateFocusedRowClass(prevKey?: string, newKey?: string) {
    if (prevKey) {
      const prevRowEl = this.gridContainerRef.current?.querySelector(
        `.ag-row[row-id="${prevKey}"]`,
      );
      prevRowEl?.classList.remove("cx-current-row");
    }

    if (newKey) {
      const newRowEl = this.gridContainerRef.current?.querySelector(
        `.ag-row[row-id="${newKey}"]`,
      );
      newRowEl?.classList.add("cx-current-row");
    }
  }

  private updateLayout(height: number, width: number) {
    if (height < 1 || width < 1) {
      return;
    }

    this.gridWidth = width;

    if (this.props.columns.length) {
      const columnsChanged: boolean = this.setColumns();

      if (columnsChanged) {
        this.gridApi.refreshHeader();
        this.gridApi.refreshCells();
        this.updateRowErrors();
      }
    }
  }

  private updateNoData(count: number) {
    const container: Element = this.agGridElementByClass("ag-body-viewport");

    if (!!count) {
      if (this.noData) {
        container.removeChild(this.noData);
        this.noData = null;
      }
    } else {
      if (!this.noData) {
        const depth: number = this.cardDepth;

        this.noData = document.createElement("div");
        this.noData.className = "cx-nodata";
        this.noData.innerText = Localization.getBuiltInMessage("noResults");
        container.appendChild<HTMLDivElement>(this.noData);
      }
    }
  }

  private updateRowErrors() {
    if (
      this.gridApi.getDisplayedRowCount() === 0 &&
      this.gridApi.getPinnedTopRowCount() === 0
    ) {
      return;
    }

    const rowErrors: Map<string, string[]> = ErrorsStore.getTableRowErrors(
      this.props.contentDataId,
      (widget) =>
        this.props.isColumnVisible(widget.widgetName, this.props.width),
    );

    const errorsSerialized = JSON.stringify(Array.from(rowErrors.entries()));

    if (this.lastRowErrors !== errorsSerialized) {
      this.lastRowErrors = errorsSerialized;
      this.removeRowErrors();
      this.rowErrorBadges.clear();
      this.rowErrorMessages.clear();

      rowErrors.forEach((messages, rowKey) => {
        let rowNode: IRowNode | undefined = this.gridApi.getRowNode(rowKey);

        if (!rowNode) {
          for (
            let index = 0;
            index < this.gridApi.getPinnedTopRowCount();
            index++
          ) {
            const pinnedRow: IRowNode = this.gridApi.getPinnedTopRow(index)!;

            if (pinnedRow.data.rowKey === rowKey) {
              rowNode = pinnedRow;
              break;
            }
          }
        }

        if (rowNode) {
          this.addRowError(rowNode, messages);
        }
      });
    }
  }

  private updateSelection() {
    if (!this.gridApi || !this.props.rowSelection) {
      return;
    }

    const count = this.getSelectedRows().length;

    this.setState({ selectionToolbarVisible: count > 0 });

    if (count === 0 && this.hadSelected) {
      this.hadSelected = false;
      Sys.announce(Localization.getBuiltInMessage("DataTable.noRowsSelected"));

      if (!this.props.isInDialog) {
        Sys.announce(
          Localization.getBuiltInMessage(
            "DataTable.headerToolbarShownInstructions",
          ),
        );
      }
    } else if (count > 0 && !this.hadSelected) {
      this.hadSelected = true;
      Sys.announce(
        Localization.getBuiltInMessage("numberSelected", {
          numSelected: count,
        }),
      );

      if (!this.props.isInDialog) {
        Sys.announce(
          Localization.getBuiltInMessage(
            "DataTable.selectionToolbarShownInstructions",
          ),
        );
      }
    } else if (count > 0) {
      Sys.announce(
        Localization.getBuiltInMessage("numberSelected", {
          numSelected: count,
        }),
      );
    }
  }

  private uploadProgress(
    file: File,
    status: ProgressStatus,
    event?: ProgressEvent,
  ) {
    let progress: HTMLDivElement | undefined = undefined;
    if (this.progressBars.has(file)) {
      progress = this.progressBars.get(file)!.firstChild as HTMLDivElement;
    }

    switch (status) {
      case "Finalized":
        if (!progress) {
          return;
        }

        const progressBar = this.progressBars.get(file)!;
        const complete = progressBar.querySelector(
          ".cx-progress-complete",
        )! as HTMLDivElement;
        complete.style.opacity = "1";
        break;

      case "Ongoing":
        if (!progress || !event) {
          return;
        }

        if (event.loaded / event.total >= 1) {
          progress.classList.add("cx-progress-slow");
          progress.style.width = "98%";

          return;
        }

        // Only show 90% complete until the upload has finished
        const pct = Math.round((event.loaded / event.total) * 90);
        progress.style.width = `${pct}%`;
        break;

      case "Started":
        // Adjusts the position of any existing progress bars.
        this.positionAllProgressBars();
        this.addProgressBar(file);
        break;

      case "Uploaded":
        if (!progress) {
          return;
        }

        progress.classList.remove("cx-progress-slow");
        progress.style.width = "100%";
        break;

      default:
    }
  }

  private get toolbarsHaveFocus(): boolean {
    return (
      (this.state.isHeaderToolbarFocused ?? false) ||
      (this.state.isFooterToolbarFocused ?? false) ||
      (this.state.isSummaryToolbarFocused ?? false)
    );
  }

  public componentDidUpdate() {
    // Must give the grid a chance to render content.
    window.clearTimeout(this.componentDidUpdateTimeout);
    this.componentDidUpdateTimeout = window.setTimeout(() => {
      this.updateRowErrors();
    }, 100);
  }

  public componentDidMount() {
    window.addEventListener("focus", this.onWindowFocus);
  }

  public componentWillUnmount() {
    window.removeEventListener("focus", this.onWindowFocus);
    window.clearTimeout(this.columnResizedTimeout);
    window.clearTimeout(this.componentDidUpdateTimeout);
    window.clearTimeout(this.gridSizeChangedTimeout);
    window.clearTimeout(this.populateDataTimeout);
    window.clearTimeout(this.populateDataWaitRenderTimeout);
    window.clearTimeout(this.removeRowErrorsTimeout);

    this.props.setPopulate(null);
    if (this.props.setRestoreLostFocus) {
      this.props.setRestoreLostFocus(null);
    }
    if (this.props.setScrollToRow) {
      this.props.setScrollToRow(null);
    }

    if (this.gridApi) {
      // FUTURE: Log a bug with ag-Grid
      // Force removal of all custom column renderers
      this.gridApi.setGridOption("columnDefs", []);
    }

    if (this.uploadFilesDisposer) {
      this.uploadFilesDisposer();
    }
  }

  public focus = () => {
    const lastFocusedCell: CellPosition | null = this.gridApi.getFocusedCell();
    const pinnedRowCount: number = this.gridApi.getPinnedTopRowCount();

    let minRow = 0;
    let maxRow = pinnedRowCount - 1;
    if (lastFocusedCell && !lastFocusedCell.rowPinned) {
      minRow = this.gridApi.getFirstDisplayedRowIndex();
      maxRow = this.gridApi.getLastDisplayedRowIndex();
    }

    if (
      lastFocusedCell &&
      lastFocusedCell.rowIndex >= minRow &&
      lastFocusedCell.rowIndex <= maxRow
    ) {
      this.gridApi.setFocusedCell(
        lastFocusedCell.rowIndex,
        lastFocusedCell.column,
        lastFocusedCell.rowPinned || undefined,
      );
    } else {
      const firstRowIndex: number =
        pinnedRowCount > 0 ? 0 : this.gridApi.getFirstDisplayedRowIndex();
      const firstColumn: Column = this.gridApi.getAllDisplayedColumns()[0];

      this.gridApi.setFocusedCell(
        firstRowIndex,
        firstColumn,
        pinnedRowCount > 0 ? "top" : undefined,
      );
    }
  };

  public focusHeaderToolbar() {
    if (this.headerToolbarRef?.current) {
      const focusable = KeyboardNavigationGroupBase.getFocusableChildren(
        this.headerToolbarRef.current,
        ToolbarBase.childSelector,
      );

      if (!!focusable.length) {
        (focusable[0] as HTMLElement).focus();
      }
    }
  }

  public focusNewRow = () => {
    this.gridApi.setFocusedCell(
      0,
      this.gridApi.getAllDisplayedColumns()[0],
      "top",
    );
  };

  public render(): React.ReactNode {
    const errorCount: number = ErrorsStore.getTableErrorCount(
      this.props.contentDataId,
    );
    const errorMessageDetails: string[] = this.getTableErrorDetails();
    const tableErrors: string[] = this.getTableErrors();
    const hideTable = this.props.hideEmptyDocument && this.state.isEmpty;
    const isFocusable: boolean =
      !this.state.isEmpty && !this.props.tableHasFocus;
    const headerToolbarVisible =
      this.props.keepHeaderOnSelect || !this.state.selectionToolbarVisible;

    // FUTURE: For future child layout only widgets that need to be displayed,
    // this logic will need to be adjusted. Also we should really define all
    // types for the ToolbarChild types.
    const selectionChildProps = this.props.selectToolbarChild as {
      props?: ToolbarChildProps;
    };
    const selectionWidgets = selectionChildProps?.props?.childWidgetNames;
    const selectionToolbarEmpty = selectionWidgets?.length == 0;

    return (
      <Box
        ref={this.tableRef}
        sx={{
          backgroundColor: "common.white",
        }}
      >
        <TableProvider visibleRows={this.state.visibleRows}>
          <FilterProvider
            filterValue={this.state.filterValue}
            onFilterChange={this.setFilterValue}
          >
            {!!errorCount || tableErrors.length > 0 ? (
              <Paper
                elevation={2}
                id={this.errorSummaryId}
                style={{ padding: 16 }}
              >
                <ErrorSummary component="div">
                  {tableErrors.map((error) => (
                    <ErrorMessage key={error} message={error} />
                  ))}
                  {!!errorCount ? (
                    <ErrorMessage
                      key="table-error-summary"
                      message={`${errorCount} ${
                        errorCount === 1
                          ? Localization.getBuiltInMessage("errorTable")
                          : Localization.getBuiltInMessage("errorsTable")
                      }`}
                    />
                  ) : null}
                </ErrorSummary>
                <div className="screenReaderOnly">
                  {errorMessageDetails.join(" ")}
                </div>
              </Paper>
            ) : null}
            <FocusTracker onFocusChanged={this.onFocusChangedHeader}>
              <KeyboardNavigationGroup
                childSelector={ToolbarBase.childSelector}
                role="toolbar"
                aria-label={
                  this.state.selectionToolbarVisible
                    ? this.selectToolbarAriaLabel
                    : this.headerToolbarAriaLabel
                }
              >
                <ToolbarsContainer
                  selectionToolbarVisible={
                    this.state.selectionToolbarVisible &&
                    !this.props.keepHeaderOnSelect
                  }
                  focused={!!this.state.isHeaderToolbarFocused}
                >
                  <selectionContext.Provider
                    value={{ selectedCount: this.selectedRows.size }}
                  >
                    {headerToolbarVisible && (
                      <ToolbarHeaderWrapper>
                        {this.headerToolbar && headerToolbarVisible && (
                          <Fade timeout={500} in={headerToolbarVisible}>
                            <NoPrintFlex ref={this.headerToolbarRef}>
                              {this.headerToolbar}
                            </NoPrintFlex>
                          </Fade>
                        )}
                      </ToolbarHeaderWrapper>
                    )}
                    {this.headerToolbar &&
                      this.selectToolbar &&
                      !selectionToolbarEmpty &&
                      !this.state.selectionToolbarVisible && <ToolbarDivider />}
                    {((selectionToolbarEmpty &&
                      this.state.selectionToolbarVisible) ||
                      !selectionToolbarEmpty) &&
                      this.selectToolbar && (
                        <ToolbarSelectionWrapper
                          selectionToolbarVisible={
                            this.state.selectionToolbarVisible
                          }
                        >
                          {this.selectToolbar && (
                            <NoPrintFlex>{this.selectToolbar}</NoPrintFlex>
                          )}
                        </ToolbarSelectionWrapper>
                      )}
                  </selectionContext.Provider>
                </ToolbarsContainer>
              </KeyboardNavigationGroup>
            </FocusTracker>
            {this.state.isGridReady && (
              <NoPrint>{this.props.dropAreaChild}</NoPrint>
            )}
            <div style={{ display: hideTable ? "none" : "block" }}>
              <div
                className={
                  "ag-theme-material" +
                  ` ${this.props.tableHasFocus ? "cx-focused" : ""}`
                }
                style={
                  {
                    "--ag-font-family": `"${BASE_FONT}", Roboto, sans-serif`,
                  } as React.CSSProperties
                }
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                onKeyDown={this.onKeyDown}
                ref={this.gridContainerRef}
              >
                <div
                  onFocus={this.onFocusTabbableElement}
                  tabIndex={isFocusable ? 0 : -1}
                />
                <div ref={this.agGridRef}>
                  <AgGridReact {...this.agProps} />
                </div>
                <div
                  onFocus={this.onFocusTabbableElement}
                  tabIndex={isFocusable ? 0 : -1}
                />
              </div>
              {this.summaryToolbar && (
                <div
                  onFocus={() => this.onFocusChangedSummary(true)}
                  onBlur={() => this.onFocusChangedSummary(false)}
                  style={{
                    marginBottom:
                      this.footerToolbar && this.props.width === "xs" ? 24 : 0,
                  }}
                >
                  {this.summaryToolbar}
                </div>
              )}
              <PaginationProvider
                currentPageIndex={this.state.pagination.currentPageIndex}
                firstRowIndex={this.state.pagination.firstRowIndex}
                lastRowIndex={this.state.pagination.lastRowIndex}
                onGoToNextPage={this.goToNextPage}
                onGoToPreviousPage={this.goToPreviousPage}
                onInitialize={this.initializePagination}
                onSetPageSize={this.setPageSize}
                pageSize={this.state.pagination.pageSize}
                pageSizes={[5, 10, 20, null]}
                scrollTargetRef={
                  this.props.disableScrollOnPageChange
                    ? undefined
                    : this.tableRef
                }
                totalPages={this.state.pagination.totalPages}
                totalRows={this.state.pagination.totalRows}
              >
                <FocusTracker onFocusChanged={this.onFocusChangedFooter}>
                  <KeyboardNavigationGroup
                    childSelector={ToolbarBase.childSelector}
                    role="toolbar"
                    aria-label={this.footerToolbarAriaLabel}
                  >
                    <ToolbarFooterWrapper
                      focused={!!this.state.isFooterToolbarFocused}
                    >
                      <NoPrint>{this.footerToolbar || null}</NoPrint>
                    </ToolbarFooterWrapper>
                  </KeyboardNavigationGroup>
                </FocusTracker>
              </PaginationProvider>
            </div>
            <div id={this.accessibleDescriptionId} style={{ display: "none" }}>
              {this.state.accessibleDescription}
            </div>
          </FilterProvider>
        </TableProvider>
      </Box>
    );
  }
}

// FUTURE
// This wrapper component was created to avoid the scope of converting this
// component to a hooks component during the MUI 5 upgrade. When the legacy
// component is converted to a hooks component, this wrapper can be removed and
// hooks it calls can be called by the converted component directly.
const Observer = observer(Table);
export default function Wrapped(props: Props): JSX.Element {
  const width = useWidth();
  const { hasFocus, setHasFocus } = React.useContext(ResponsiveGridContext);
  return (
    <Observer
      {...props}
      width={width}
      tableHasFocus={hasFocus}
      setTableHasFocus={setHasFocus}
    />
  );
}
